// Onglet Consignes : lecture du Markdown simplifié et recherche sans accents.
//
// Markdown reconnu :
//   ## Titre            sous-titre toujours visible
//   ### Titre           bloc repliable (article, rubrique)
//   - point / "  - "    liste, un niveau d'imbrication
//   1. étape             liste numérotée (étapes)
//   > texte             encadré « attention »
//   **gras**, *italique*, [texte](tel:… | mailto:… | https://…)

const SAFE_LINK = /^(tel:\+?[0-9 ]{3,20}|mailto:[^\s<>"']+@[^\s<>"']+|https:\/\/[^\s<>"']+)$/i;

// keepLength : même longueur que le texte d'origine (pour surligner), sans œ → oe.
export function foldText(value, keepLength = false) {
  const base = String(value || '')
    .normalize('NFC')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return keepLength ? base : base.replace(/œ/g, 'oe').replace(/æ/g, 'ae');
}

export function safeHref(href) {
  const value = String(href || '').trim();
  return SAFE_LINK.test(value) ? value : null;
}

// Découpe une ligne en morceaux : texte, gras, italique, lien.
export function parseInline(text) {
  const out = [];
  const source = String(text || '');
  const pattern = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match;
  while ((match = pattern.exec(source))) {
    if (match.index > last) out.push({ t: 'text', v: source.slice(last, match.index) });
    if (match[1] !== undefined) out.push({ t: 'b', v: match[1] });
    else if (match[2] !== undefined) out.push({ t: 'i', v: match[2] });
    else {
      const href = safeHref(match[4]);
      out.push(href ? { t: 'link', v: match[3], href } : { t: 'text', v: match[3] });
    }
    last = match.index + match[0].length;
  }
  if (last < source.length) out.push({ t: 'text', v: source.slice(last) });
  return out;
}

export function plainText(text) {
  return parseInline(text).map((part) => part.v).join('');
}

// Blocs : h2, article (title + blocks), p, list (items: {text, children}), callout.
export function parseMarkdown(markdown) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const root = [];
  let target = root;
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (paragraph.length) target.push({ type: 'p', text: paragraph.join(' ') });
    paragraph = [];
  };
  const flushList = () => {
    if (list) target.push(list);
    list = null;
  };
  const flush = () => {
    flushParagraph();
    flushList();
  };

  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) {
      flush();
      return;
    }
    let match = line.match(/^##\s+(.+)$/);
    if (match && !line.startsWith('###')) {
      flush();
      target = root;
      root.push({ type: 'h2', text: match[1].trim() });
      return;
    }
    match = line.match(/^###\s+(.+)$/);
    if (match) {
      flush();
      const article = { type: 'article', title: match[1].trim(), blocks: [] };
      root.push(article);
      target = article.blocks;
      return;
    }
    match = line.match(/^>\s?(.*)$/);
    if (match) {
      flush();
      const previous = target[target.length - 1];
      if (previous && previous.type === 'callout' && previous.open) previous.text += ` ${match[1]}`;
      else target.push({ type: 'callout', text: match[1], open: true });
      return;
    }
    match = line.match(/^(\s*)(?:[-•]|(\d{1,2})[.)])\s+(.+)$/);
    if (match) {
      flushParagraph();
      const indent = match[1].length;
      const ordered = Boolean(match[2]);
      const nested = indent >= 2 && list && list.items.length > 0;
      if (nested) {
        list.items[list.items.length - 1].children.push(match[3]);
        return;
      }
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { type: 'list', ordered, items: [] };
      list.items.push({ text: match[3], children: [], number: ordered ? Number(match[2]) : null });
      return;
    }
    if (list && /^\s{2,}\S/.test(raw) && list.items.length) {
      // Suite d'un point de liste sur la ligne suivante.
      const item = list.items[list.items.length - 1];
      if (item.children.length) item.children[item.children.length - 1] += ` ${line.trim()}`;
      else item.text += ` ${line.trim()}`;
      return;
    }
    flushList();
    paragraph.push(line.trim());
  });
  flush();
  const close = (blocks) => blocks.forEach((block) => {
    if (block.type === 'callout') delete block.open;
    if (block.type === 'article') close(block.blocks);
  });
  close(root);
  return root;
}

// Une entrée par bloc lisible, avec son article et son sous-titre.
export function indexSection(section) {
  const entries = [];
  let heading = null;
  const push = (text, article, key) => {
    const plain = plainText(text);
    entries.push({ text: plain, folded: foldText(plain), article, heading, key });
  };
  const walk = (blocks, article, prefix) => {
    blocks.forEach((block, index) => {
      const key = `${prefix}${index}`;
      if (block.type === 'h2') {
        heading = plainText(block.text);
        push(block.text, null, key);
      } else if (block.type === 'article') {
        push(block.title, block.title, key);
        walk(block.blocks, block.title, `${key}.`);
      } else if (block.type === 'list') {
        block.items.forEach((item, itemIndex) => {
          push(item.text, article, `${key}.${itemIndex}`);
          item.children.forEach((child, childIndex) => push(child, article, `${key}.${itemIndex}.${childIndex}`));
        });
      } else {
        push(block.text, article, key);
      }
    });
  };
  walk(parseMarkdown(section.body), null, '');
  return entries;
}

function queryWords(query) {
  return foldText(query).split(/\s+/).map((word) => word.trim()).filter(Boolean);
}

export function snippetAround(text, query, radius = 70) {
  const words = queryWords(query);
  const folded = foldText(text);
  const at = words.length ? folded.indexOf(words[0]) : -1;
  if (at < 0 || text.length <= radius * 2) return text.length > radius * 2 ? `${text.slice(0, radius * 2)}…` : text;
  const start = Math.max(0, at - radius);
  const end = Math.min(text.length, at + words[0].length + radius);
  return `${start > 0 ? '…' : ''}${text.slice(start, end)}${end < text.length ? '…' : ''}`;
}

// Tous les mots de la recherche doivent apparaître dans le bloc (sans accents, sans casse).
export function searchConsignes(sections, query, limit = 60) {
  const words = queryWords(query);
  if (!words.length || words.join('').length < 2) return [];
  const results = [];
  (sections || []).forEach((section) => {
    indexSection(section).forEach((entry) => {
      if (results.length >= limit) return;
      if (words.every((word) => entry.folded.includes(word))) {
        results.push({
          sectionId: section.id,
          sectionTitle: section.title,
          article: entry.article,
          heading: entry.heading,
          key: entry.key,
          snippet: snippetAround(entry.text, query),
        });
      }
    });
  });
  return results;
}

// Recherche d'étudiants : chambre (début du numéro) ou nom et prénom dans n'importe quel ordre.
export function searchStudents(students, query) {
  const list = Array.isArray(students) ? students : [];
  const words = queryWords(query);
  if (!words.length) return list;
  const scored = [];
  list.forEach((student) => {
    const room = foldText(student.room).replace(/\s+/g, '');
    const name = foldText(`${student.last_name} ${student.first_name}`);
    const extra = foldText(`${student.class_name || ''} ${student.floor || ''}`);
    const compact = words.join('');
    let score = 0;
    if (room && room === compact) score = 3;
    else if (room && room.startsWith(compact)) score = 2;
    else if (words.every((word) => name.includes(word) || extra.split(/\s+/).includes(word))) score = 1;
    if (score) scored.push({ student, score });
  });
  return scored
    .sort((a, b) => b.score - a.score
      || foldText(a.student.last_name).localeCompare(foldText(b.student.last_name))
      || foldText(a.student.first_name).localeCompare(foldText(b.student.first_name)))
    .map((entry) => entry.student);
}

// Repère les passages à surligner dans un texte (indices dans le texte d'origine).
export function highlightRanges(text, query) {
  const words = foldText(query, true).split(/\s+/).filter((word) => word.length >= 2);
  if (!words.length) return [];
  const folded = foldText(String(text || '').normalize('NFC'), true);
  const ranges = [];
  words.forEach((word) => {
    let from = 0;
    let at;
    while ((at = folded.indexOf(word, from)) >= 0) {
      ranges.push([at, at + word.length]);
      from = at + word.length;
    }
  });
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  ranges.forEach((range) => {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([...range]);
  });
  return merged;
}
